import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Search, Filter, DollarSign,
  Plus, Edit, Trash2, Eye, EyeOff, X,
  GraduationCap, Crown, ToggleLeft, ToggleRight,
  TrendingUp, Repeat, RefreshCw,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 차트 색상 팔레트 (burgundy/gold 계열 — 앱 테마와 일치)
const CHART_COLORS = ["#722F37", "#C76E7E", "#D4A24E", "#9C3D4A", "#E0B973", "#8C6B4F"];
const STATUS_COLORS = {
  completed: "#10b981",
  pending: "#f59e0b",
  failed: "#ef4444",
  cancelled: "#a8a29e",
};
const STATUS_KO = { completed: "완료", pending: "대기", failed: "실패", cancelled: "취소" };

// PayPal 실시간 주문 상태 → 한글 라벨/색상
const LIVE_STATUS_KO = {
  COMPLETED: { label: "결제완료", cls: "text-emerald-600" },
  APPROVED: { label: "승인됨(미결제)", cls: "text-amber-600" },
  CREATED: { label: "미결제(주문만 생성)", cls: "text-stone-500" },
  SAVED: { label: "미결제(저장)", cls: "text-stone-500" },
  PAYER_ACTION_REQUIRED: { label: "결제자 액션 필요", cls: "text-amber-600" },
  VOIDED: { label: "무효", cls: "text-rose-500" },
  EXPIRED: { label: "만료/결제안됨", cls: "text-rose-500" },
  NO_ORDER: { label: "주문번호 없음", cls: "text-stone-400" },
};
const TYPE_KO = {
  annual_membership: "연간 멤버십",
  matching_consulting: "매칭 컨설팅",
  one_time_service: "1회성 서비스",
  event_ticket: "이벤트 티켓",
  special_program: "특별 프로그램",
};

const TABS = [
  { id: "payments", label: "결제 내역", icon: DollarSign },
  { id: "subscriptions", label: "정기결제(구독)", icon: Repeat },
  { id: "plans",    label: "동적 가격 항목", icon: GraduationCap },
  { id: "legacy",   label: "Legacy 회원 관리", icon: Crown },
];

// 구독 상태 배지 (PayPal subscription status)
const SUB_STATUS_BADGE = {
  active:           { label: "활성(결제중)", cls: "bg-emerald-100 text-emerald-700" },
  approved:         { label: "승인됨", cls: "bg-emerald-100 text-emerald-700" },
  approval_pending: { label: "승인대기", cls: "bg-amber-100 text-amber-700" },
  suspended:        { label: "일시중지", cls: "bg-orange-100 text-orange-700" },
  cancelled:        { label: "취소됨", cls: "bg-stone-100 text-stone-600" },
  expired:          { label: "만료", cls: "bg-red-100 text-red-700" },
};

const STATUS_BADGE = {
  completed: { label: "완료", cls: "bg-emerald-100 text-emerald-700" },
  pending:   { label: "대기", cls: "bg-amber-100 text-amber-700" },
  on_hold:   { label: "보류(On hold)", cls: "bg-amber-100 text-amber-700" },
  failed:    { label: "실패", cls: "bg-red-100 text-red-700" },
  cancelled: { label: "취소", cls: "bg-stone-100 text-stone-600" },
};

const PLAN_TYPES = [
  { value: "one_time_service", label: "1회성 서비스" },
  { value: "event_ticket",     label: "이벤트 티켓" },
  { value: "special_program",  label: "특별 프로그램" },
];

const emptyPlan = {
  label: "",
  price: 0,
  type: "event_ticket",
  description: "",
  is_active: true,
  sort_order: 0,
};

export default function AdminPayPalPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading, token } = useAuth();
  const [activeTab, setActiveTab] = useState("payments");

  const [payments, setPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsLiveSyncing, setPaymentsLiveSyncing] = useState(false);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState(emptyPlan);

  const [legacyUsers, setLegacyUsers] = useState([]);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const [subscriptions, setSubscriptions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsLiveSyncing, setSubsLiveSyncing] = useState(false);
  const [subsSummary, setSubsSummary] = useState({ total: 0, active: 0, paid_at_least_once: 0 });

  const authHeader = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [backfilling, setBackfilling] = useState(false);
  const handleBackfillMembership = useCallback(async () => {
    if (!token) return;
    setBackfilling(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/paypal/backfill-annual-membership`, {
        method: "POST",
        headers: authHeader(),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "교정 실패");
      toast.success(d.message || "회원 등급 교정 완료");
      if (activeTab === "payments") fetchPayments();
    } catch (e) {
      toast.error(e.message || "회원 등급 교정 실패");
    } finally {
      setBackfilling(false);
    }
  }, [token, authHeader, activeTab]);

  const fetchSubscriptions = useCallback(async (live = false) => {
    if (!token) return;
    if (live) setSubsLiveSyncing(true); else setSubsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/paypal/subscriptions?limit=300${live ? "&live=true" : ""}`, { headers: authHeader() });
      const d = await res.json();
      setSubscriptions(d.subscriptions || []);
      setSubsSummary({ total: d.total || 0, active: d.active || 0, paid_at_least_once: d.paid_at_least_once || 0 });
      if (live) toast.success("PayPal에서 실시간 상태를 동기화했습니다.");
    } catch {
      toast.error("구독 내역 조회 실패");
    } finally {
      setSubsLoading(false); setSubsLiveSyncing(false);
    }
  }, [token, authHeader]);

  const fetchPayments = useCallback(async (live = false) => {
    if (!token) return;
    if (live) setPaymentsLiveSyncing(true); else setPaymentsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (live) params.append("live", "true");
      const url = `${API_URL}/api/admin/paypal/payments${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await fetch(url, { headers: authHeader() });
      const d = await res.json();
      setPayments(d.payments || []);
      setTotalRevenue(d.total_completed_usd || 0);
      if (live) toast.success("PayPal에서 각 주문의 실제 상태를 대조했습니다.");
    } catch {
      toast.error("결제 내역 조회 실패");
    } finally { setPaymentsLoading(false); setPaymentsLiveSyncing(false); }
  }, [token, authHeader, statusFilter]);

  // 분석 차트용 — 상태 필터와 무관하게 전체 결제 데이터 조회
  const fetchAllPayments = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/paypal/payments?limit=1000`, { headers: authHeader() });
      const d = await res.json();
      setAllPayments(d.payments || []);
    } catch { /* 차트는 선택적 — 실패 시 무시 */ }
  }, [token, authHeader]);

  const fetchPlans = useCallback(async () => {
    if (!token) return;
    setPlansLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/paypal/dynamic-plans`, { headers: authHeader() });
      const d = await res.json();
      setPlans(d.plans || []);
    } catch { toast.error("동적 가격 항목 조회 실패"); }
    finally { setPlansLoading(false); }
  }, [token, authHeader]);

  const fetchLegacy = useCallback(async () => {
    if (!token) return;
    setLegacyLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users?is_legacy=true&limit=200`, { headers: authHeader() });
      const d = await res.json();
      setLegacyUsers(d.users || d || []);
    } catch { /* fallback empty */ }
    finally { setLegacyLoading(false); }
  }, [token, authHeader]);

  const searchUsers = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await fetch(`${API_URL}/api/admin/users?search=${encodeURIComponent(q)}&limit=20`, { headers: authHeader() });
      const d = await res.json();
      setSearchResults(d.users || d || []);
    } catch { setSearchResults([]); }
  }, [authHeader]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || user?.role !== "admin") { navigate("/"); return; }
    if (activeTab === "payments") { fetchPayments(); fetchAllPayments(); }
    if (activeTab === "subscriptions") fetchSubscriptions(false);
    if (activeTab === "plans") fetchPlans();
    if (activeTab === "legacy") fetchLegacy();
  }, [activeTab, authLoading, isAuthenticated, user?.role, navigate,
      fetchPayments, fetchAllPayments, fetchPlans, fetchLegacy, fetchSubscriptions]);

  // === 분석 차트 데이터 (전체 결제 기준) ===
  const analytics = useMemo(() => {
    const completed = allPayments.filter((p) => p.status === "completed");

    // 1) 일자별 매출 추이 (완료 결제, 최근 14개 활동일)
    const byDate = {};
    completed.forEach((p) => {
      const d = (p.captured_at || p.created_at || "").slice(0, 10);
      if (!d) return;
      byDate[d] = (byDate[d] || 0) + (Number(p.amount_usd) || 0);
    });
    const revenueTrend = Object.keys(byDate).sort().slice(-14).map((d) => ({
      date: d.slice(5), // MM-DD
      revenue: Math.round(byDate[d] * 100) / 100,
    }));

    // 2) 상품 유형별 매출 (완료 결제)
    const byType = {};
    completed.forEach((p) => {
      const t = p.type || "기타";
      byType[t] = (byType[t] || 0) + (Number(p.amount_usd) || 0);
    });
    const revenueByType = Object.keys(byType).map((t) => ({
      name: TYPE_KO[t] || t,
      value: Math.round(byType[t] * 100) / 100,
    }));

    // 3) 상태 분포 (전체)
    const byStatus = {};
    allPayments.forEach((p) => {
      const s = p.status || "unknown";
      byStatus[s] = (byStatus[s] || 0) + 1;
    });
    const statusDist = Object.keys(byStatus).map((s) => ({
      name: STATUS_KO[s] || s,
      key: s,
      value: byStatus[s],
    }));

    return {
      revenueTrend,
      revenueByType,
      statusDist,
      completedCount: completed.length,
      pendingCount: allPayments.filter((p) => p.status === "pending").length,
      hasData: completed.length > 0,
    };
  }, [allPayments]);

  const openCreatePlan = () => { setEditingPlan(null); setPlanForm({ ...emptyPlan, sort_order: plans.length }); setShowPlanForm(true); };
  const openEditPlan = (p) => { setEditingPlan(p); setPlanForm({ ...emptyPlan, ...p }); setShowPlanForm(true); };
  const closePlanForm = () => { setShowPlanForm(false); setEditingPlan(null); setPlanForm(emptyPlan); };

  const submitPlan = async (e) => {
    e.preventDefault();
    if (!planForm.label.trim()) return toast.error("항목명을 입력해주세요.");
    if (!planForm.price || planForm.price <= 0) return toast.error("가격을 입력해주세요 (USD).");
    try {
      const url = editingPlan
        ? `${API_URL}/api/admin/paypal/dynamic-plans/${editingPlan.id}`
        : `${API_URL}/api/admin/paypal/dynamic-plans`;
      const res = await fetch(url, {
        method: editingPlan ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ ...planForm, price: parseFloat(planForm.price) }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "저장 실패");
      }
      toast.success(editingPlan ? "수정되었습니다." : "추가되었습니다.");
      closePlanForm();
      fetchPlans();
    } catch (err) { toast.error(err.message); }
  };

  const togglePlanActive = async (p) => {
    try {
      await fetch(`${API_URL}/api/admin/paypal/dynamic-plans/${p.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ is_active: !p.is_active }),
      });
      fetchPlans();
    } catch { toast.error("상태 변경 실패"); }
  };

  const deletePlan = async (p) => {
    if (!window.confirm(`"${p.label}" 항목을 삭제하시겠습니까?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/paypal/dynamic-plans/${p.id}`, { method: "DELETE", headers: authHeader() });
      if (!res.ok) throw new Error("삭제 실패");
      toast.success("삭제되었습니다.");
      fetchPlans();
    } catch (err) { toast.error(err.message); }
  };

  const toggleLegacy = async (userId, next) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/legacy`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ is_legacy: next }),
      });
      if (!res.ok) throw new Error("변경 실패");
      toast.success(next ? "Legacy 회원으로 지정되었습니다." : "Legacy 해제되었습니다.");
      fetchLegacy();
      setSearchResults((prev) => prev.map((u) => u.id === userId ? { ...u, is_legacy: next } : u));
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/admin")} className="text-stone-500 hover:text-stone-800 p-2 rounded-lg hover:bg-white transition" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">PayPal 결제 관리</h1>
            <p className="text-xs text-stone-500 mt-0.5">결제 내역 · 동적 가격 항목 · Legacy 회원 토글</p>
          </div>
          <button
            onClick={handleBackfillMembership}
            disabled={backfilling}
            className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition disabled:opacity-50"
            data-testid="backfill-membership-btn"
            title="연간 멤버십을 결제했지만 등급이 일반회원으로 남아있는 회원을 정회원으로 일괄 교정합니다."
          >
            {backfilling ? "교정 중…" : "결제 회원 등급 일괄 교정"}
          </button>
        </div>

        <div className="mb-6 flex items-center gap-1 bg-white rounded-xl p-1 border border-stone-200 w-fit" data-testid="paypal-admin-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${active ? "bg-burgundy-500 text-white shadow" : "text-stone-600 hover:bg-stone-50"}`}
                data-testid={`tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {activeTab === "payments" && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-2xl p-5 border border-stone-200">
                <p className="text-xs text-stone-500 mb-1">완료된 결제 총액</p>
                <p className="text-2xl font-bold text-stone-900">USD ${totalRevenue.toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-stone-200">
                <p className="text-xs text-stone-500 mb-1">전체 트랜잭션</p>
                <p className="text-2xl font-bold text-stone-900">{payments.length}건</p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-stone-200 flex items-center justify-between">
                <div>
                  <p className="text-xs text-stone-500 mb-1">상태 필터</p>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-sm border border-stone-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-burgundy-400" data-testid="status-filter">
                    <option value="">전체</option>
                    <option value="completed">완료</option>
                    <option value="pending">대기</option>
                    <option value="failed">실패</option>
                    <option value="cancelled">취소</option>
                  </select>
                </div>
                <Filter className="w-5 h-5 text-stone-400" />
              </div>
            </div>

            {/* === 분석 차트 (Recharts) === */}
            <div className="mb-6" data-testid="paypal-analytics">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-burgundy-500" />
                <h2 className="text-base font-bold text-stone-800">결제 분석</h2>
              </div>

              {!analytics.hasData ? (
                <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center text-stone-400 text-sm" data-testid="analytics-empty">
                  완료된 결제가 쌓이면 매출 추이·상품별 매출·상태 분포 차트가 표시됩니다.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* 일자별 매출 추이 */}
                  <div className="bg-white rounded-2xl border border-stone-200 p-5 lg:col-span-2" data-testid="chart-revenue-trend">
                    <p className="text-sm font-semibold text-stone-700 mb-4">일자별 매출 추이 (USD · 완료 기준)</p>
                    <ResponsiveContainer width="100%" height={260}>
                      <AreaChart data={analytics.revenueTrend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#722F37" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#722F37" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f0ee" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#78716c" }} />
                        <YAxis tick={{ fontSize: 11, fill: "#78716c" }} />
                        <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, "매출"]} />
                        <Area type="monotone" dataKey="revenue" stroke="#722F37" strokeWidth={2} fill="url(#revGradient)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  {/* 상품 유형별 매출 */}
                  <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="chart-revenue-by-type">
                    <p className="text-sm font-semibold text-stone-700 mb-4">상품 유형별 매출 (USD)</p>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={analytics.revenueByType} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f0ee" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#78716c" }} interval={0} />
                        <YAxis tick={{ fontSize: 11, fill: "#78716c" }} />
                        <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, "매출"]} />
                        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                          {analytics.revenueByType.map((entry, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* 결제 상태 분포 */}
                  <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="chart-status-dist">
                    <p className="text-sm font-semibold text-stone-700 mb-4">결제 상태 분포 (건수)</p>
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie
                          data={analytics.statusDist}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={(e) => `${e.name} ${e.value}`}
                          labelLine={false}
                        >
                          {analytics.statusDist.map((entry, i) => (
                            <Cell key={i} fill={STATUS_COLORS[entry.key] || CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-xs text-stone-500">
                ⚠️ <span className="font-medium text-stone-600">대기</span> 상태는 주문만 생성되고 실제 결제는 안 된 건입니다. 정확한 확인은 "PayPal 실시간 확인"을 눌러주세요.
              </p>
              <button
                onClick={() => fetchPayments(true)}
                disabled={paymentsLiveSyncing}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                data-testid="payments-live-sync-btn"
              >
                {paymentsLiveSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Repeat className="w-3.5 h-3.5" />}
                PayPal 실시간 확인
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              {paymentsLoading ? (
                <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-burgundy-500" /></div>
              ) : payments.length === 0 ? (
                <div className="p-12 text-center text-stone-500">결제 내역이 없습니다.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3 text-left">일시</th>
                        <th className="px-4 py-3 text-left">상품</th>
                        <th className="px-4 py-3 text-left">결제자</th>
                        <th className="px-4 py-3 text-right">금액 (USD)</th>
                        <th className="px-4 py-3 text-center">상태</th>
                        <th className="px-4 py-3 text-left">PayPal Order</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {payments.map((p) => {
                        const badge = STATUS_BADGE[p.status] || { label: p.status, cls: "bg-stone-100 text-stone-600" };
                        return (
                          <tr key={p.id} className="hover:bg-stone-50/50" data-testid={`payment-row-${p.id}`}>
                            <td className="px-4 py-3 text-stone-700 whitespace-nowrap">
                              {p.captured_at ? new Date(p.captured_at).toLocaleString("ko-KR", { dateStyle: "short", timeStyle: "short" }) : new Date(p.created_at).toLocaleString("ko-KR", { dateStyle: "short", timeStyle: "short" })}
                            </td>
                            <td className="px-4 py-3 font-medium text-stone-900">{p.label}</td>
                            <td className="px-4 py-3 text-stone-600">
                              {p.user_email || p.user_name ? (
                                <div>
                                  <div className="font-medium text-stone-800">{p.user_email || "(이메일 없음)"}</div>
                                  {(p.real_name || p.user_name) && (
                                    <div className="text-[11px] text-stone-400">{p.real_name || p.user_name}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-stone-400">(비회원)</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-stone-900">${p.amount_usd?.toLocaleString()}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
                              {p.live_status && (
                                <div className={`text-[10px] mt-1 font-semibold ${LIVE_STATUS_KO[p.live_status]?.cls || "text-stone-400"}`} data-testid={`payment-live-${p.id}`}>
                                  PayPal: {LIVE_STATUS_KO[p.live_status]?.label || p.live_status}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-stone-400 text-[11px] font-mono">{p.paypal_order_id?.slice(0, 12)}…</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "subscriptions" && (
          <div data-testid="subscriptions-tab">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <div className="bg-white rounded-2xl p-5 border border-stone-200">
                <p className="text-xs text-stone-500 mb-1">전체 구독</p>
                <p className="text-2xl font-bold text-stone-900">{subsSummary.total}건</p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-stone-200">
                <p className="text-xs text-stone-500 mb-1">활성 구독</p>
                <p className="text-2xl font-bold text-emerald-600">{subsSummary.active}건</p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-stone-200">
                <p className="text-xs text-stone-500 mb-1">실제 결제 발생(1회+)</p>
                <p className="text-2xl font-bold text-stone-900">{subsSummary.paid_at_least_once}건</p>
              </div>
            </div>

            <div className="mb-4 bg-blue-50/60 border border-blue-200 rounded-xl p-4 text-xs text-blue-900 leading-relaxed">
              <p className="font-semibold mb-1">ℹ️ &quot;PayPal 실시간 동기화&quot;를 누르면 각 구독의 실제 결제 상태를 PayPal에서 직접 가져옵니다.</p>
              <p className="text-blue-800">· <b>최근 결제</b> 칸에 금액/시간이 표시되면 실제로 청구가 완료된 것입니다.<br />· 카드사 문자의 <b>&quot;KRW 0&quot;</b>는 PayPal의 카드 검증(인증)일 뿐 실제 청구가 아닙니다. 실제 정기결제는 PayPal이 청구 주기에 별도로 처리하며, 반영까지 다소 시간이 걸릴 수 있습니다.</p>
            </div>

            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-sm text-stone-500">회원 입력 표시금액(원/달러)과 실제 PayPal 청구액(USD)을 함께 보여줍니다.</p>
              <Button onClick={() => fetchSubscriptions(true)} disabled={subsLiveSyncing} className="bg-burgundy-500 hover:bg-burgundy-600 text-white" data-testid="subs-live-sync-btn">
                {subsLiveSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                PayPal 실시간 동기화
              </Button>
            </div>

            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              {subsLoading ? (
                <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-burgundy-500" /></div>
              ) : subscriptions.length === 0 ? (
                <div className="p-12 text-center text-stone-500">정기결제(구독) 내역이 없습니다.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3 text-left">회원</th>
                        <th className="px-4 py-3 text-right">표시금액</th>
                        <th className="px-4 py-3 text-right">실제청구(USD)</th>
                        <th className="px-4 py-3 text-center">상태</th>
                        <th className="px-4 py-3 text-left">최근 결제</th>
                        <th className="px-4 py-3 text-left">다음 청구일</th>
                        <th className="px-4 py-3 text-left">생성일</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {subscriptions.map((s) => {
                        const statusKey = (s.live_status || s.status || "").toLowerCase();
                        const badge = SUB_STATUS_BADGE[statusKey] || { label: statusKey || "-", cls: "bg-stone-100 text-stone-600" };
                        const disp = s.display_currency === "KRW"
                          ? `₩${Number(s.display_amount || 0).toLocaleString()}`
                          : `$${Number(s.display_amount || 0).toLocaleString()}`;
                        return (
                          <tr key={s.subscription_id || s.id} className="hover:bg-stone-50/50" data-testid={`subscription-row-${s.subscription_id || s.id}`}>
                            <td className="px-4 py-3 text-stone-700">
                              <div className="font-medium text-stone-900">{s.user_email || "(이메일 없음)"}</div>
                              {(s.real_name || s.user_name) && (
                                <div className="text-[11px] text-stone-500">{s.real_name || s.user_name}</div>
                              )}
                              <div className="text-[11px] text-stone-400 font-mono">{(s.subscription_id || "").slice(0, 16)}…</div>
                            </td>
                            <td className="px-4 py-3 text-right text-stone-700">{disp}<span className="text-[10px] text-stone-400 ml-1">/월</span></td>
                            <td className="px-4 py-3 text-right font-semibold text-stone-900">${Number(s.amount || 0).toLocaleString()}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
                              {s.live_error && <div className="text-[10px] text-red-400 mt-1">동기화 오류</div>}
                            </td>
                            <td className="px-4 py-3 text-stone-600 whitespace-nowrap">
                              {s.last_payment_amount
                                ? <span className="text-emerald-700 font-medium">${Number(s.last_payment_amount).toLocaleString()} <span className="text-[10px] text-stone-400">{s.last_payment_time ? new Date(s.last_payment_time).toLocaleDateString("ko-KR") : ""}</span></span>
                                : <span className="text-stone-300">— (동기화 필요)</span>}
                            </td>
                            <td className="px-4 py-3 text-stone-500 whitespace-nowrap text-xs">
                              {s.next_billing_time ? new Date(s.next_billing_time).toLocaleDateString("ko-KR") : "—"}
                            </td>
                            <td className="px-4 py-3 text-stone-400 whitespace-nowrap text-xs">
                              {s.created_at ? new Date(s.created_at).toLocaleDateString("ko-KR") : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "plans" && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-stone-500">
                이벤트 티켓 / 특별 프로그램 가격 항목 — 활성화하면 <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs">/pricing</code> 페이지에 자동 노출.
              </p>
              <Button onClick={openCreatePlan} className="bg-burgundy-500 hover:bg-burgundy-600 text-white" data-testid="create-plan-btn">
                <Plus className="w-4 h-4 mr-2" /> 새 항목 등록
              </Button>
            </div>
            {plansLoading ? (
              <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-burgundy-500" /></div>
            ) : plans.length === 0 ? (
              <div className="p-12 text-center bg-white rounded-2xl border border-stone-200 text-stone-500">
                등록된 항목이 없습니다.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans.map((p) => (
                  <div key={p.id} className={`bg-white rounded-2xl border p-5 transition ${p.is_active ? "border-stone-200" : "border-stone-200 opacity-60"}`} data-testid={`plan-card-${p.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="text-[10px] uppercase tracking-wider text-stone-400">
                          {PLAN_TYPES.find((t) => t.value === p.type)?.label || p.type}
                        </span>
                        <h3 className="text-base font-semibold text-stone-900 mt-1">{p.label}</h3>
                      </div>
                      <span className="text-xl font-bold text-stone-900 whitespace-nowrap">${p.price.toLocaleString()}</span>
                    </div>
                    {p.description && <p className="text-xs text-stone-500 mb-3 line-clamp-2">{p.description}</p>}
                    <div className="flex items-center gap-2 mt-3">
                      <button onClick={() => togglePlanActive(p)} className={`flex-1 inline-flex items-center justify-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition ${p.is_active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`} data-testid={`plan-toggle-${p.id}`}>
                        {p.is_active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        {p.is_active ? "노출중" : "숨김"}
                      </button>
                      <button onClick={() => openEditPlan(p)} className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"><Edit className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deletePlan(p)} className="px-3 py-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "legacy" && (
          <div>
            <div className="mb-5 bg-blue-50/60 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
              <p className="font-semibold mb-1">💡 Legacy 회원이란?</p>
              <p className="text-blue-800">기존 사이트에서 월간 결제로 이용하시던 회원입니다. 이 회원들만 월간 정기결제(legacy monthly plan)를 이용할 수 있습니다.</p>
            </div>
            <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-5">
              <p className="text-sm font-semibold text-stone-700 mb-3">회원 검색 후 Legacy 지정/해제</p>
              <div className="relative">
                <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input type="text" value={userSearch} onChange={(e) => { setUserSearch(e.target.value); searchUsers(e.target.value); }} className="w-full pl-9 pr-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400 text-sm" placeholder="이름·이메일·닉네임 (2글자 이상)" data-testid="legacy-search-input" />
              </div>
              {searchResults.length > 0 && (
                <div className="mt-3 divide-y divide-stone-100 border border-stone-100 rounded-lg max-h-72 overflow-y-auto">
                  {searchResults.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-3 hover:bg-stone-50">
                      <div className="text-sm">
                        <p className="font-medium text-stone-800">{u.name || u.nickname || "(이름 없음)"}</p>
                        <p className="text-xs text-stone-500">{u.email}</p>
                      </div>
                      <button onClick={() => toggleLegacy(u.id, !u.is_legacy)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold ${u.is_legacy ? "bg-amber-100 text-amber-800 hover:bg-amber-200" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`} data-testid={`legacy-toggle-${u.id}`}>
                        {u.is_legacy ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                        {u.is_legacy ? "Legacy" : "지정"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-2xl border border-stone-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-stone-800">현재 Legacy 회원 ({legacyUsers.length}명)</h3>
              </div>
              {legacyLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-stone-400 my-8" />
              ) : legacyUsers.length === 0 ? (
                <p className="text-sm text-stone-400 text-center py-8">아직 Legacy 회원이 없습니다.</p>
              ) : (
                <div className="divide-y divide-stone-100">
                  {legacyUsers.map((u) => (
                    <div key={u.id} className="flex items-center justify-between py-3" data-testid={`legacy-user-${u.id}`}>
                      <div className="text-sm">
                        <p className="font-medium text-stone-800">{u.name || u.nickname || "(이름 없음)"}</p>
                        <p className="text-xs text-stone-500">{u.email}</p>
                      </div>
                      <button onClick={() => toggleLegacy(u.id, false)} className="text-xs text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg">
                        Legacy 해제
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showPlanForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={closePlanForm}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitPlan} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" data-testid="plan-form-modal">
            <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-stone-800">{editingPlan ? "항목 수정" : "새 항목 등록"}</h2>
              <button type="button" onClick={closePlanForm} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-1 block">분류</label>
                <select value={planForm.type} onChange={(e) => setPlanForm({ ...planForm, type: e.target.value })} className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400">
                  {PLAN_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-1 block">항목명 *</label>
                <input type="text" value={planForm.label} onChange={(e) => setPlanForm({ ...planForm, label: e.target.value })} placeholder="예: 6월 글로벌 줌미팅 이벤트 티켓" className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400" required />
              </div>
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-1 block">가격 (USD) *</label>
                <input type="number" step="0.01" value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} placeholder="예: 50" className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400" required />
              </div>
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-1 block">설명 (선택)</label>
                <textarea value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-stone-700 mb-1 block">노출 순서</label>
                  <input type="number" value={planForm.sort_order ?? 0} onChange={(e) => setPlanForm({ ...planForm, sort_order: parseInt(e.target.value, 10) || 0 })} className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={planForm.is_active} onChange={(e) => setPlanForm({ ...planForm, is_active: e.target.checked })} className="w-4 h-4" />
                    <span className="text-sm text-stone-700 font-medium">/pricing 에 노출</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-stone-100 flex justify-end gap-2 bg-stone-50">
              <Button type="button" variant="outline" onClick={closePlanForm}>취소</Button>
              <Button type="submit" className="bg-burgundy-500 hover:bg-burgundy-600 text-white">
                {editingPlan ? "수정 저장" : "항목 등록"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
